import { SIMULATION_TIMING } from "../config/simulation.ts";
import {
  advanceFixedStepClock,
  createFixedStepClock,
  type FixedStepClock,
} from "../core/time.ts";

export interface GameLoopFrame {
  readonly interpolationAlpha: number;
  readonly fps: number;
  readonly sampleCount: number;
}

export interface GameLoop {
  start(): void;
  stop(): void;
  resetTiming(): void;
  getFrameStats(): Readonly<{ fps: number; sampleCount: number }>;
}

export interface GameLoopOptions {
  readonly step: (fixedDeltaSeconds: number) => void;
  readonly render: (frame: GameLoopFrame) => void;
}

const FPS_WINDOW_SIZE = 90;

export function createGameLoop(options: GameLoopOptions): GameLoop {
  let animationFrameId = 0;
  let previousTimestampMilliseconds: number | null = null;
  let fixedStepClock: FixedStepClock = createFixedStepClock();
  const frameDurationsSeconds: number[] = [];
  let fps = 60;
  let sampleCount = 0;

  function recordFrame(frameDeltaSeconds: number): void {
    if (frameDeltaSeconds <= 0) {
      return;
    }

    frameDurationsSeconds.push(frameDeltaSeconds);
    if (frameDurationsSeconds.length > FPS_WINDOW_SIZE) {
      frameDurationsSeconds.shift();
    }
    const total = frameDurationsSeconds.reduce(
      (sum, duration) => sum + duration,
      0,
    );
    fps = total > 0 ? frameDurationsSeconds.length / total : 60;
    sampleCount += 1;
  }

  function onAnimationFrame(timestampMilliseconds: number): void {
    const frameDeltaSeconds =
      previousTimestampMilliseconds === null
        ? 0
        : Math.min(
            (timestampMilliseconds - previousTimestampMilliseconds) / 1_000,
            SIMULATION_TIMING.maximumFrameDeltaSeconds,
          );
    previousTimestampMilliseconds = timestampMilliseconds;
    recordFrame(frameDeltaSeconds);

    const advance = advanceFixedStepClock(
      fixedStepClock,
      frameDeltaSeconds,
      options.step,
      SIMULATION_TIMING.fixedStepSeconds,
      SIMULATION_TIMING.maximumCatchUpSteps,
    );
    fixedStepClock = advance.clock;
    options.render({
      interpolationAlpha: advance.interpolationAlpha,
      fps,
      sampleCount,
    });
    animationFrameId = requestAnimationFrame(onAnimationFrame);
  }

  return {
    start(): void {
      if (animationFrameId !== 0) {
        return;
      }
      animationFrameId = requestAnimationFrame(onAnimationFrame);
    },

    stop(): void {
      if (animationFrameId !== 0) {
        cancelAnimationFrame(animationFrameId);
        animationFrameId = 0;
      }
      previousTimestampMilliseconds = null;
    },

    resetTiming(): void {
      previousTimestampMilliseconds = null;
      fixedStepClock = createFixedStepClock();
      frameDurationsSeconds.length = 0;
      fps = 60;
      sampleCount = 0;
    },

    getFrameStats(): Readonly<{ fps: number; sampleCount: number }> {
      return { fps, sampleCount };
    },
  };
}
